import React, { useRef, useState } from "react";
import RestAPI from "../../../utils/restAPI";
import { logError } from "../../../shared/common/logger";
import { toastNotify } from "../../../shared/client/FrontendUtils";

interface ImageUploadFieldProps {
	label: string;
	imagePath: string | undefined;
	organizationId: string;
	uploadDestination: string;
	onImageChange: (path: string) => void;
	onImageRemove: () => void;
	altText?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
	label,
	imagePath,
	organizationId,
	uploadDestination,
	onImageChange,
	onImageRemove,
	altText = "Image preview",
}) => {
	const uploadRef = useRef<HTMLInputElement>(null);
	const [inputKey, setInputKey] = useState(0);
	const [uploading, setUploading] = useState(false);

	const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (uploadRef.current?.files && uploadRef.current.files.length > 0) {
			const file = uploadRef.current.files[0];
			if (!ALLOWED_TYPES.includes(file.type)) {
				toastNotify("Only PNG, JPG/JPEG, or SVG images are allowed.", true);
				setInputKey((v) => v + 1);
				return;
			}
			setUploading(true);
			try {
				const formData = new FormData();
				formData.append("destination", `organizations/${organizationId}/pdfConfig/${uploadDestination}`);
				formData.append("files", uploadRef.current.files[0]);
				const data = await RestAPI.fetchAPI("/rest/files/upload", "POST", formData);
				onImageChange(data[0].storagePath);
				setInputKey((v) => v + 1);
			} catch (error) {
				logError(`Error uploading ${label.toLowerCase()}`, error);
				toastNotify(`Error uploading ${label.toLowerCase()}`, true);
			} finally {
				setUploading(false);
			}
		}
	};

	return (
		<div>
			<label className="block text-gray-700 text-sm font-medium mb-1">{label}</label>
			<div className="flex items-start gap-4">
				{imagePath ? (
					<div className="relative">
						<img
							src={RestAPI.getStorageFileUrl(imagePath)}
							alt={altText}
							className="max-w-[500px] max-h-[300px] object-contain border border-gray-300 rounded-lg"
						/>
						<button
							type="button"
							onClick={onImageRemove}
							className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
						>
							×
						</button>
					</div>
				) : (
					<div className="w-[250px] h-[150px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-500 text-sm">
						No image
					</div>
				)}
				<div className="flex flex-col gap-2">
					<button
						type="button"
						onClick={() => uploadRef.current?.click()}
						disabled={uploading}
						className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-xs whitespace-nowrap"
					>
						{uploading ? "Uploading..." : imagePath ? "Change" : "Upload"}
					</button>
					{imagePath && (
						<span className="text-xs text-gray-500 break-all max-w-[150px]">{imagePath}</span>
					)}
				</div>
			</div>
			<input
				key={inputKey}
				type="file"
				ref={uploadRef}
				accept="image/png,image/jpeg,image/svg+xml"
				style={{ display: "none" }}
				onChange={handleFileChange}
			/>
		</div>
	);
};

export default ImageUploadField;

